import { useFeatureFlags } from '@chronosphereio/core';
import { Close as CloseIcon } from 'mdi-material-ui';
import { Box, BoxProps } from '../Box';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { DrawerContextValue, useDrawerContext } from './DrawerContextProvider';

export interface DrawerHeaderProps extends Pick<BoxProps, 'id'> {
  /**
   * Title of the drawer, appears on the left of the header.
   */
  title: string;
  /**
   * Use this prop **only** when you need to customize the actions that appear on the right of the header.
   *
   * In most cases, prefer using the standard `variant` prop, which handles actions automatically:
   * - `create` and `edit` variants show primary and secondary action buttons.
   * - `detail` variant shows a close IconButton.
   *
   * Providing `actions` overrides this default behavior and should be used sparingly.
   */
  actions?: React.ReactNode;
  /**
   * Badge shown to the right of the title. Useful for highlighting something about the content of the drawer (e.g.
   * new feature, internal only). It is recommended to use a "small" badge for this use case.
   */
  badge?: React.ReactNode;
  /**
   * Click handler for primary action button. If not provided, the primary button will not be rendered.
   */
  onPrimaryButtonClick?: () => void;
  /**
   * Click handler for the secondary "Cancel" button.
   */
  onSecondaryButtonClick?: () => void;
  /**
   * Whether the primary action button is disabled.
   * @default false
   */
  primaryButtonDisabled?: boolean;
  /**
   * Use this prop **only** when you need to customize the primary button text.
   *
   * In most cases, prefer using the standard `variant` prop, which handles the button text automatically:
   * - `create` variants use "Create"
   * - `edit` variants use "Save"
   */
  primaryButtonText?: string;
  /**
   * Tooltip for the primary button.
   */
  primaryButtonTooltip?: React.ReactNode;
  /**
   * A slot for putting elements before the title. E.g. can be used to put an icon or button before the title.
   */
  titlePrefix?: React.ReactNode;

  /**
   * Variant for the header. Needed for some rare legacy use cases that use `Drawer.Header` without a `Drawer`.
   *
   * @deprecated The variant should NOT be set on `Drawer.Header` in new usages. Instead set `variant` on the parent
   * `Drawer`.
   */
  variant?: DrawerContextValue['variant'];
}

export function DrawerHeader({
  title,
  actions,
  badge,
  primaryButtonDisabled = false,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButtonText,
  primaryButtonTooltip = '',
  id,
  titlePrefix,
  variant: variantProp = 'edit',
}: DrawerHeaderProps) {
  const drawerContext = useDrawerContext();

  // Try to use the drawer context and fall back to the prop if it is not set. As noted in the jsdoc for the variant
  // prop, this exists as a cheap workaround for a legacy use case.
  const variant: DrawerContextValue['variant'] = drawerContext?.variant || variantProp;

  return (
    <Box
      alignItems="center"
      justifyContent="space-between"
      sx={{
        display: 'flex',
        padding: (theme) => theme.spacing(1, 2),
        borderBottom: (theme) =>
          `1px solid ${theme.featureFlags.panwRebrandStubYear ? theme.palette.border.default : theme.palette.divider}`,
        backgroundColor: (theme) =>
          theme.featureFlags.panwRebrandStubYear ? theme.palette.background.popover : undefined,
      }}
      id={id}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {titlePrefix}
        <Typography variant="h2">{title}</Typography>
        {badge && (
          // Div needed around badge for vertical centering to work because badge is usually an inline
          // element.
          <div>{badge}</div>
        )}
      </Stack>
      <Stack direction="row" spacing={1}>
        {actions ?? (
          <DefaultActions
            variant={variant}
            onPrimaryButtonClick={onPrimaryButtonClick}
            onSecondaryButtonClick={onSecondaryButtonClick}
            primaryButtonDisabled={primaryButtonDisabled}
            primaryButtonText={primaryButtonText}
            primaryButtonTooltip={primaryButtonTooltip}
          />
        )}
      </Stack>
    </Box>
  );
}

function DefaultActions(
  props: Pick<
    DrawerHeaderProps,
    | 'onPrimaryButtonClick'
    | 'onSecondaryButtonClick'
    | 'primaryButtonDisabled'
    | 'primaryButtonText'
    | 'primaryButtonTooltip'
  > &
    Pick<DrawerContextValue, 'variant'>
) {
  const {
    variant,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    primaryButtonDisabled,
    primaryButtonText,
    primaryButtonTooltip,
  } = props;
  const { panwRebrandStubYear } = useFeatureFlags();

  if (variant === 'detail') {
    return (
      <IconButton onClick={onSecondaryButtonClick} aria-label="Close drawer">
        <CloseIcon />
      </IconButton>
    );
  }

  const defaultText = variant === 'create' ? 'Create' : 'Save';
  const primaryText = primaryButtonText ?? defaultText;

  const cancelButton = (
    <Button variant="secondary" onClick={onSecondaryButtonClick}>
      Cancel
    </Button>
  );

  const primaryButton = onPrimaryButtonClick ? (
    <ButtonTooltip content={primaryButtonTooltip}>
      <Button disabled={primaryButtonDisabled} onClick={onPrimaryButtonClick} type="submit">
        {primaryText}
      </Button>
    </ButtonTooltip>
  ) : null;

  if (panwRebrandStubYear) {
    return (
      <>
        {cancelButton}
        {primaryButton}
      </>
    );
  }

  return (
    <>
      {primaryButton}
      {cancelButton}
    </>
  );
}

function ButtonTooltip(props: { children: React.ReactNode; content: React.ReactNode }): React.ReactNode {
  const { content, children } = props;
  return content ? (
    <Tooltip Content={content}>
      {/*
       Tooltip needs to listen to the child element's events to display the title, and if the button is disabled
       it does not fire events.
     */}
      <span>{children}</span>
    </Tooltip>
  ) : (
    children
  );
}
